import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { BarChart2, Home } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './MortgageCalculatorPage/components/CalculationForm';
import ResultsOverview from './MortgageCalculatorPage/components/ResultsOverview';
import PaymentSchedule from './MortgageCalculatorPage/components/PaymentSchedule';
import QuickAdjustmentSidebar from './MortgageCalculatorPage/components/QuickAdjustmentSidebar';
import MortgageProgramsSidebar from './MortgageCalculatorPage/components/MortgageProgramsSidebar';
import TipsSidebar from './MortgageCalculatorPage/components/TipsSidebar';
import InfoContent from './MortgageCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  propertyPrice: '6000000',
  initialPayment: '1500000',
  loanTerm: '20', // Years
  interestRate: '7.5', // Annual percentage
  paymentType: 'annuity', // 'annuity' or 'differentiated'
  creditType: 'standard',
  includeEarlyRepayment: false,
  earlyRepaymentType: 'reduction', // 'reduction' (term) or 'payment' (monthly payment)
  earlyRepaymentAmount: '100000',
  earlyRepaymentPeriod: 'monthly', // 'monthly', 'quarterly', 'semi_annually', 'annually', 'once'
  earlyRepaymentStartMonth: '12', // Month number (1-based)
  includeRefinancing: false,
  refinancingMonth: '60', // Month number (1-based)
  refinancingRate: '6.0', // New annual percentage
  includeInsurance: false,
  insuranceRate: '0.5', // Annual rate as percentage of *remaining balance*
  includeOtherCosts: false,
  otherCostsAmount: '10000', // One-time costs at the beginning
};

// Определение данных кредитных программ
export const creditTypes = [
  { id: 'standard', name: 'Стандартная', defaultRate: 7.5 },
  { id: 'government', name: 'Господдержка 2020', defaultRate: 8.0 },
  { id: 'family', name: 'Семейная', defaultRate: 6.0 },
  { id: 'far_east', name: 'Дальневосточная', defaultRate: 2.0 },
  { id: 'it', name: 'IT-специалистам', defaultRate: 5.0 },
  { id: 'agricultural', name: 'Сельская', defaultRate: 3.0 },
  { id: 'military', name: 'Военная', defaultRate: 7.0 }
];

// Определение периодов досрочного погашения
export const earlyRepaymentPeriods = [
  { id: 'monthly', name: 'Ежемесячно' },
  { id: 'quarterly', name: 'Ежеквартально' },
  { id: 'semi_annually', name: 'Раз в полгода' },
  { id: 'annually', name: 'Ежегодно' },
  { id: 'once', name: 'Разово' }
];

/**
 * MortgageCalculatorPage Component - A comprehensive and interactive mortgage calculator.
 */
const MortgageCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('mortgage-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Ипотечный калькулятор | Gensio';
  const metaDescription = tool?.metaDescription || 'Полный ипотечный калькулятор онлайн: расчет ежемесячного платежа (аннуитетный, дифференцированный), переплаты, построение графика платежей с учетом досрочного погашения, рефинансирования, страхования, господдержки и других льготных программ.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [showFullSchedule, setShowFullSchedule] = useState(false);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      const findRate = (id) => {
        const found = creditTypes.find(t => t.id === id);
        return found ? found.defaultRate.toString() : initialFormData.interestRate;
      };

      switch (subToolId) {
        case 'early-repayment':
          newState.includeEarlyRepayment = true;
          break;
        case 'refinance':
          newState.includeRefinancing = true;
          break;
        case 'differentiated':
          newState.paymentType = 'differentiated';
          break;
        case 'family':
          newState.creditType = 'family';
          newState.interestRate = findRate('family');
          break;
        case 'it':
          newState.creditType = 'it';
          newState.interestRate = findRate('it');
          break;
        case 'government':
          newState.creditType = 'government';
          newState.interestRate = findRate('government');
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setSchedule([]);
    setErrors({});
    setShowFullSchedule(false);

  }, [subToolId]);

  // Расчет доли первоначального взноса в процентах от стоимости
  const initialPaymentPercent = useMemo(() => {
    const price = parseFloat(formData.propertyPrice) || 0;
    const initial = parseFloat(formData.initialPayment) || 0;
    if (price > 0 && initial >= 0) {
      return Math.min(100, Math.max(0, (initial / price) * 100));
    }
    return 0;
  }, [formData.propertyPrice, formData.initialPayment]);

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Home className="w-8 h-8 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                {tool?.name || 'Ипотечный калькулятор'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              schedule={schedule}
              setSchedule={setSchedule}
              creditTypes={creditTypes}
              earlyRepaymentPeriods={earlyRepaymentPeriods}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} />
                <PaymentSchedule 
                  schedule={schedule} 
                  results={results}
                  showFullSchedule={showFullSchedule}
                  setShowFullSchedule={setShowFullSchedule}
                />
              </>
            )}

            {/* --- Информационный контент о ипотеке --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              initialPaymentPercent={initialPaymentPercent}
              errors={errors}
              isCalculating={isCalculating}
              setResults={setResults}
              setSchedule={setSchedule}
              setErrors={setErrors}
            />

            {/* --- Панель программ ипотеки --- */}
            <MortgageProgramsSidebar 
              formData={formData}
              setFormData={setFormData}
              creditTypes={creditTypes}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Scrollbar for Payment Schedule Table */
        .payment-schedule-table {
          scrollbar-width: thin;
          scrollbar-color: theme('colors.gray.400') theme('colors.gray.100');
        }
        .dark .payment-schedule-table {
          scrollbar-color: theme('colors.gray.600') theme('colors.gray.800');
        }
        .payment-schedule-table::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .payment-schedule-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.100');
          border-radius: 10px;
        }
        .dark .payment-schedule-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.800');
        }
        .payment-schedule-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.400');
          border-radius: 10px;
          border: 2px solid theme('colors.gray.100');
        }
        .dark .payment-schedule-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.600');
          border-color: theme('colors.gray.800');
        }
        .payment-schedule-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
        .dark .payment-schedule-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
      `}</style>
    </div>
  );
};

export default MortgageCalculatorPage;